import dayjs from 'dayjs';
import { EffectReducer } from 'use-effect-reducer';
import { Airport } from '../InteractiveMapContainer/filters/useLocations';
import { Regions } from '../InteractiveFullscreenMapContainer/types';

export interface FiltersState {
  originDestinationLogic: string;
  origin: Partial<Airport>;
  destination: Partial<Airport>;
  destinationRegion?: Regions;
  dateRange: any;
  monthInterval?: { startMonth?: string; endMonth?: string };
  budget: { maximum: number; minimum: number } | null;
  travelClass: string;
  sorting: string;
  flightType: string;
  flightStopCount: string;
  redemptionUnit?: string;
  currency?: string;
  formPrepopuled: boolean;
  userInteraction?: boolean;
  airlines?: any;
  travelerInterest?: $TSFixMe;
  histogramType?: string;
  noClearDestination?: boolean;
  navigation?: string;
  isFullMode?: boolean;
  passengers?: any;
  travelInterestChangeConfirmed?: boolean;
  travelInterestChangeProcessed?: boolean;
  lastUserAction?: string;
  isMobile?: boolean;
  tripDuration?: { minimum: number; maximum: number };
}

const handleFlightTypeChange = (state: FiltersState, action: any): FiltersState => {
  let dateRange;
  let histogramType;
  const isMobile = state?.isMobile;
  if (action.payload === 'ROUND_TRIP') {
    // one way to round trip
    let monthData = {};
    if (state.dateRange?.departureDate) {
      if (isMobile) {
        monthData = {
          start: dayjs(state.dateRange.departureDate).toDate(),
          end: dayjs(state.dateRange.departureDate).endOf('month').toDate()
        };
      } else {
        monthData = {
          start: dayjs(state.dateRange.start).toDate(),
          end: dayjs(state.dateRange.start).add(1, 'month').endOf('month').toDate()
        };
      }
    }
    dateRange = {
      ...state.dateRange,
      action: 'FLIGHT_TYPE_CHANGE_TO_ROUND_TRIP',
      departureDate: state.dateRange.departureDate,
      ...monthData
    };
    histogramType = state.dateRange.departureDate ? 'INBOUND' : 'OUTBOUND';
  } else {
    //round trip to one way
    let monthData = {};
    if (state.dateRange?.departureDate) {
      let startDate = dayjs(state.dateRange.departureDate).startOf('month').toDate();
      if (dayjs(startDate).month() === dayjs().month()) {
        startDate = dayjs().toDate();
      }
      if (isMobile) {
        monthData = {
          start: startDate,
          end: dayjs(startDate).endOf('month').toDate()
        };
      } else {
        let startDate = dayjs(state.dateRange.start).startOf('month').toDate();
        if (dayjs(startDate).month() === dayjs().month()) {
          startDate = dayjs().toDate();
        }
        monthData = {
          start: startDate,
          end: dayjs(startDate).add(1, 'month').endOf('month').toDate()
        };
      }
    } else {
      let start = dayjs(state.dateRange.start).startOf('month').toDate();
      if (dayjs(start).month() === dayjs().month()) {
        start = dayjs().toDate();
      }
      monthData = {
        start
      };
    }
    dateRange = {
      ...state.dateRange,
      action: 'FLIGHT_TYPE_CHANGE_TO_ONE_WAY',
      returnDate: undefined,
      ...monthData
    };
    histogramType = 'OUTBOUND';
  }
  return {
    ...state,
    flightType: action.payload,
    dateRange,
    histogramType
  };
};

export const filtersReducer: EffectReducer<FiltersState, any, any> = (state, action, exec) => {
  exec({ type: 'tracking', action, prevState: state });
  if (action.category !== 'tracking') {
    switch (action.type) {
      case 'NAVIGATION_CHANGE': {
        return {
          ...state,
          navigation: action.payload
        };
      }
      case 'FULL_MODE': {
        return {
          ...state,
          isFullMode: action.payload
        };
      }
      case 'MONTH_CHANGE_HISTOGRAM': {
        return {
          ...state,
          dateRange: {
            start: action.payload.start,
            end: action.payload.end,
            selected: action.payload.selected
          }
        };
      }
      case 'MONTH_CHANGE':
      case 'DATE_RANGE_CHANGE': {
        return {
          ...state,
          dateRange: action.payload
        };
      }
      case 'CHANGE_MONTH_INTERVAL': {
        let { startMonth = '', endMonth = '' } = action.payload;

        startMonth = dayjs(startMonth).startOf('month').toDate();
        endMonth = dayjs(endMonth).endOf('month').toDate();

        return {
          ...state,
          monthInterval: { startMonth, endMonth }
          // dateRange: {
          //   ...state.dateRange,
          //   start: startMonth,
          //   end: endMonth
          // }
        };
      }
      case 'DATE_RANGE_CLEAR': {
        return {
          ...state,
          dateRange: {}
        };
      }
      case 'DATE_RANGE_HISTOGRAM_CHANGE': {
        return {
          ...state,
          dateRange: action.payload.dateRange,
          histogramType: action.payload.histogramType
        };
      }
      case 'MONTH_CHANGE_HISTOGRAM_BACK':
      case 'MONTH_CHANGE_HISTOGRAM_FORWARD': {
        return {
          ...state,
          dateRange: {
            start: action.payload.start,
            end: action.payload.end,
            departureDate: action.payload.departureDate,
            returnDate: action.payload.returnDate,
            action: action.payload.action
          }
        };
      }
      case 'MODIFY_DEPARTURE_DATE_HISTOGRAM': {
        return {
          ...state,
          dateRange: {
            ...state.dateRange,
            departureDate: action.payload.departureDate
          }
        };
      }
      case 'MODIFY_ACTION_HISTOGRAM': {
        return {
          ...state,
          dateRange: {
            ...state.dateRange,
            action: action.payload.action
          }
        };
      }
      case 'DATE_RANGE_HISTOGRAM_TYPE_CHANGE': {
        return {
          ...state,
          histogramType: action.payload.histogramType
        };
      }
      case 'BUDGET_CHANGE': {
        return {
          ...state,
          budget: action.payload
        };
      }
      case 'BUDGET_CLEAR': {
        return {
          ...state,
          budget: null
        };
      }
      case 'CHANGE_TRAVEL_INTEREST_CONFIRMATION': {
        return {
          ...state,
          travelInterestChangeConfirmed: action.payload
        };
      }
      case 'TRAVEL_INTEREST_CHANGE_PROCESSED': {
        return {
          ...state,
          travelInterestChangeProcessed: action.payload
        };
      }
      case 'FULLY_VACCINATED_CHANGE': {
        return {
          ...state,
          fullyVaccinated: action.payload
        };
      }
      case 'FLIGHT_TYPE_CHANGE': {
        if (state.dateRange?.action) {
          return handleFlightTypeChange(state, action);
        }
        return {
          ...state,
          flightType: action.payload,
          dateRange:
            state.histogramType !== null && state.histogramType !== undefined
              ? {
                  ...state.dateRange,
                  start:
                    dayjs(state?.dateRange?.start).get('month') === dayjs(new Date()).get('month')
                      ? new Date()
                      : dayjs(state?.dateRange?.start).set('date', 1).toDate()
                }
              : state.dateRange,
          histogramType:
            state.histogramType !== null && state.histogramType !== undefined
              ? ''
              : state.histogramType
        };
      }
      case 'TRIP_DURATION_CHANGE': {
        return {
          ...state,
          tripDuration: action.payload
        };
      }
      case 'NUMBER_STOPS_CHANGE': {
        if (state.dateRange?.action) {
          return {
            ...state,
            dateRange: {
              ...state.dateRange,
              action: `${state.dateRange.action}_filter-change`
            },
            flightStopCount: action.payload
          };
        }
        return {
          ...state,
          flightStopCount: action.payload
        };
      }
      case 'REDEMPTION_UNIT_CHANGE': {
        if (state.dateRange?.action) {
          return {
            ...state,
            dateRange: {
              ...state.dateRange,
              action: `${state.dateRange.action}_filter-change`
            },
            redemptionUnit: action.payload
          };
        }
        return {
          ...state,
          redemptionUnit: action.payload
        };
      }
      case 'CURRENCY_CHANGE': {
        return {
          ...state,
          currency: action.payload
        };
      }
      case 'RATING_CHANGE': {
        return {
          ...state,
          rating: action.payload
        };
      }
      case 'SORTING_CHANGE': {
        return {
          ...state,
          sorting: action.payload.value
        };
      }
      case 'TRAVEL_CLASS_CHANGE': {
        if (state.dateRange?.action) {
          return {
            ...state,
            dateRange: {
              ...state.dateRange,
              action: `${state.dateRange.action}_filter-change`
            },
            travelClass: action.payload
          };
        }
        return {
          ...state,
          travelClass: action.payload
        };
      }
      case 'TRAVEL_CLASS_CHANGE_NO_TRACKING': {
        return {
          ...state,
          travelClass: action.payload
        };
      }
      case 'AIRLINES_CHANGE': {
        return {
          ...state,
          airlines: action.payload
        };
      }
      case 'TRAVELER_CHANGE': {
        return {
          ...state,
          userInteraction: true,
          travelerInterest: {
            ...state.travelerInterest,
            ...action.payload
          }
        };
      }
      case 'TRAVELER_CLEAR': {
        const travelerInterest = {
          ...state.travelerInterest
        };
        delete travelerInterest[action.payload];
        return {
          ...state,
          travelerInterest
        };
      }
      case 'ORIGIN_CHANGE': {
        const originValue = action.payload || {};
        let destinationValue = state.destination;

        if (destinationValue.value === originValue.value) {
          destinationValue = {};
        }

        if (state?.dateRange?.action) {
          let dateRange = { ...state.dateRange };
          if (state.dateRange?.departureDate) {
            if (state?.isMobile) {
              dateRange = {
                start:
                  dayjs().month() === dayjs(state.dateRange.end).month()
                    ? dayjs().toDate()
                    : dayjs(state.dateRange.end).startOf('month').toDate(),
                end: state.dateRange.end,
                action: '-'
              };
            } else {
              const startValue =
                dayjs().month() === dayjs(state.dateRange.start).month()
                  ? dayjs().toDate()
                  : dayjs(state.dateRange.start).startOf('month').toDate();
              dateRange = {
                start: startValue,
                end: dayjs(startValue).add(1, 'month').endOf('month').toDate(),
                action: '-'
              };
            }
          }
          return {
            ...state,
            dateRange,
            origin: originValue,
            destination: destinationValue,
            userInteraction: true,
            airlines: originValue.value !== '' && destinationValue.value !== '' ? undefined : null
          };
        }
        let newReturn = {
          ...state,
          origin: originValue,
          destination: destinationValue,
          userInteraction: true,
          airlines: originValue.value !== '' && destinationValue.value !== '' ? undefined : null,
          dateRange:
            state.histogramType !== null &&
            state.histogramType !== undefined &&
            state.histogramType !== ''
              ? {
                  start: dayjs().toDate(),
                  end: dayjs().endOf('month').toDate()
                }
              : state.dateRange
        };
        if (state.histogramType !== null && state.histogramType !== undefined) {
          newReturn['histogramType'] = '';
        }
        return newReturn;
      }
      case 'ORIGIN_CLEAR': {
        if (state?.dateRange?.action) {
          let dateRange = { ...state.dateRange };
          if (state.dateRange?.departureDate) {
            if (state?.isMobile) {
              dateRange = {
                start:
                  dayjs().month() === dayjs(state.dateRange.end).month()
                    ? dayjs().toDate()
                    : dayjs(state.dateRange.end).startOf('month').toDate(),
                end: state.dateRange.end,
                action: '-'
              };
            } else {
              const startValue =
                dayjs().month() === dayjs(state.dateRange.start).month()
                  ? dayjs().toDate()
                  : dayjs(state.dateRange.start).startOf('month').toDate();
              dateRange = {
                start: startValue,
                end: dayjs(startValue).add(1, 'month').endOf('month').toDate(),
                action: '-'
              };
            }
          }
          return {
            ...state,
            dateRange,
            origin: {},
            airlines: undefined
          };
        }
        let newReturn = {
          ...state,
          origin: {},
          airlines: undefined,
          dateRange:
            state.histogramType !== null &&
            state.histogramType !== undefined &&
            state.histogramType !== ''
              ? {
                  start: dayjs().toDate(),
                  end: dayjs().endOf('month').toDate()
                }
              : state.dateRange
        };
        if (state.histogramType !== null && state.histogramType !== undefined) {
          newReturn['histogramType'] = '';
        }
        return newReturn;
      }
      case 'DESTINATION_CHANGE': {
        const destinationValue = action.payload || {};
        let originValue = state.origin;
        if (destinationValue.value === originValue.value) {
          originValue = {};
        }
        if (state?.dateRange?.action) {
          let dateRange = { ...state.dateRange };
          if (state.dateRange?.departureDate) {
            if (state?.isMobile) {
              dateRange = {
                start:
                  dayjs().month() === dayjs(state.dateRange.end).month()
                    ? dayjs().toDate()
                    : dayjs(state.dateRange.end).startOf('month').toDate(),
                end: state.dateRange.end,
                action: '-'
              };
            } else {
              const startValue =
                dayjs().month() === dayjs(state.dateRange.start).month()
                  ? dayjs().toDate()
                  : dayjs(state.dateRange.start).startOf('month').toDate();
              dateRange = {
                start: startValue,
                end: dayjs(startValue).add(1, 'month').endOf('month').toDate(),
                action: '-'
              };
            }
          }
          return {
            ...state,
            dateRange,
            destination: destinationValue,
            origin: originValue,
            userInteraction: true,
            airlines: originValue.value !== '' && destinationValue.value !== '' ? undefined : null
          };
        }
        let newReturn = {
          ...state,
          destination: destinationValue,
          origin: originValue,
          userInteraction: true,
          airlines: originValue.value !== '' && destinationValue.value !== '' ? undefined : null,
          dateRange:
            state.histogramType !== null &&
            state.histogramType !== undefined &&
            state.histogramType !== ''
              ? {
                  start: dayjs().toDate(),
                  end: dayjs().endOf('month').toDate()
                }
              : state.dateRange
        };

        if (state.histogramType !== null && state.histogramType !== undefined) {
          newReturn['histogramType'] = '';
        }

        return newReturn;
      }
      case 'AUTO_CLEAR_DESTINATION':
      case 'DESTINATION_CLEAR': {
        if (state?.dateRange?.action) {
          let dateRange = { ...state.dateRange };
          if (state.dateRange?.departureDate) {
            if (state?.isMobile) {
              dateRange = {
                start:
                  dayjs().month() === dayjs(state.dateRange.end).month()
                    ? dayjs().toDate()
                    : dayjs(state.dateRange.end).startOf('month').toDate(),
                end: state.dateRange.end,
                action: '-'
              };
            } else {
              const startValue =
                dayjs().month() === dayjs(state.dateRange.start).month()
                  ? dayjs().toDate()
                  : dayjs(state.dateRange.start).startOf('month').toDate();
              dateRange = {
                start: startValue,
                end: dayjs(startValue).add(1, 'month').endOf('month').toDate(),
                action: '-'
              };
            }
          }
          return {
            ...state,
            dateRange,
            destination: {},
            airlines: undefined
          };
        }
        let newReturn = {
          ...state,
          destination: {},
          airlines: undefined,
          dateRange:
            state.histogramType !== null &&
            state.histogramType !== undefined &&
            state.histogramType !== ''
              ? {
                  start: dayjs().toDate(),
                  end: dayjs().endOf('month').toDate()
                }
              : state.dateRange
        };

        if (state.histogramType !== null && state.histogramType !== undefined) {
          newReturn['histogramType'] = '';
        }
        return newReturn;
      }
      case 'SWAP_LOCATIONS': {
        return {
          ...state,
          origin: state.destination,
          destination: state.origin
        };
      }
      case 'CLEAR_ALL': {
        return {
          ...state,
          origin: {},
          destination: {},
          dateRange: {},
          budget: null,
          airlines: undefined,
          tripDuration: undefined,
          rating: undefined,
          travelerInterest: {},
          ...action.payload
        };
      }
      case 'CLEAR_PARTIAL': {
        if (state.dateRange?.action) {
          if (state.flightType !== action.payload.flightType) {
            const data = handleFlightTypeChange(state, { payload: action.payload.flightType });
            let histogramType = action.payload.histogramType;
            if (action.payload.flightType === 'ONE_WAY') {
              histogramType = 'OUTBOUND';
            } else {
              histogramType = 'INBOUND';
            }
            return {
              ...state,
              ...action.payload,
              dateRange: {
                ...data.dateRange
              },
              histogramType
            };
          } else {
            return {
              ...state,
              dateRange: {
                ...state.dateRange,
                action: `${state.dateRange.action}_filter-change`
              },
              ...action.payload
            };
          }
        }
        return {
          ...state,
          ...action.payload
        };
      }
      case 'SET_ALL': {
        return {
          ...state,
          ...action.payload
        };
      }
      case 'SET_USER_INTERACTION': {
        return {
          ...state,
          userInteraction: true
        };
      }
      case 'SET_LAST_USER_ACTION_CHANGED': {
        return {
          ...state,
          lastUserAction: action.payload
        };
      }
      case 'PASSENGERS_CHANGE': {
        return {
          ...state,
          passengers: action.payload
        };
      }
      case 'ACCEPT_SUGGESTION': {
        return {
          ...state,
          prompt: action.payload
        };
      }
      case 'SELECT_REGION':
      case 'REGION_CHANGE': {
        return {
          ...state,
          destinationRegion: action.payload
        };
      }
    }
  }
  return state;
};
